/**
 * 차단 목록 패널 — 사이드바 "차단 목록" 활성 시 우측 패널에 표시.
 *
 * <h3>역할</h3>
 * <p>내가 추가한 차단 규칙 (발신 주소 / 도메인 / 제목 키워드 / 본문 키워드 / 정규식) 을 한 화면에서
 * 보고, 일괄 해제할 수 있다. 차단 추가는 메일 상세 화면의 "발신자 차단" / "도메인 차단" 메뉴에서 진행.
 *
 * <h3>왜 별도 탭인가</h3>
 * <p>"내 연락처" 와 "차단 목록" 은 정책의 양면 — 함께 두면 시각적 혼란. 사용자 요청 (2026-05-05) 으로
 * 사이드바 별도 탭으로 분리. ContactsPanel 은 연락처만, BlockListPanel 은 차단만 담당.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
    Box, Chip, IconButton, Paper, Stack, Switch, TableBody, TableCell, TableHead,
    TableRow, Tooltip, Typography,
} from "@mui/material";
import { ds } from "../../../../styles/designSystem";
import RefreshRoundedIcon   from "@mui/icons-material/RefreshRounded";
import DeleteRoundedIcon    from "@mui/icons-material/DeleteRounded";
import BlockRoundedIcon     from "@mui/icons-material/BlockRounded";
import PublicRoundedIcon    from "@mui/icons-material/PublicRounded";

import { mailApi }  from "../../api/mailApi";
import { notify }   from "../../../../util/toast";
import { confirm }  from "../../../../util/confirmDialog";
import LoadingDots from "../../../../component/common/LoadingDots";
import ResizableTable from "../../../../component/common/ResizableTable";
import TableToolbar from "../../../../component/common/TableToolbar";
import { useTableSearch } from "../../../../util/tableSearch";

/** 차단 규칙 타입 → 한글 라벨 */
const RULE_TYPE_LABEL = {
    ADDRESS:         "발신 주소",
    DOMAIN:          "도메인",
    SUBJECT_KEYWORD: "제목 키워드",
    BODY_KEYWORD:    "본문 키워드",
    REGEX:           "정규식",
};

/**
 * 액션 → 한글 라벨.
 * <p>백엔드가 저장하는 값은 MailBlockRule.ACTION_SPAM("SPAM") 하나뿐이다(그 외 값은 서버가 400).
 * 예전 라벨 맵(BLOCK/MOVE_TO_SPAM/…)은 실제 값과 하나도 맞지 않아 늘 원시값 "SPAM" 이 그대로 노출됐다.
 * 미지원 값은 아래 fallback 으로 원시값을 그대로 보여준다.
 */
const ACTION_LABEL = {
    SPAM: "스팸함 이동",
};

/**
 * 규칙이 실제로 동작하는지 — 백엔드 DTO 의 supported 필드 기준.
 * <p>BODY_KEYWORD 는 본문을 매칭하지 않아 항상 false. 필드가 없는 구버전 응답은 ruleType 으로 직접 판정.
 */
function isRuleSupported(r) {
    if (typeof r.supported === "boolean") return r.supported;
    return r.ruleType !== "BODY_KEYWORD";
}

export default function BlockListPanel({ isAdmin = false }) {
    const [rules, setRules] = useState([]);
    /** 전사(GLOBAL) 규칙 — MAIL_RULE_GLOBAL 권한자에게만 조회·표시한다. */
    const [globalRules, setGlobalRules] = useState([]);
    // 표 안 검색 — 공통 유틸이 행 전체를 훑는다.
    const [tableQuery, setTableQuery] = useState("");
    const rulesFiltered = useTableSearch(rules, tableQuery);
    const [loading, setLoading] = useState(false);

    // isAdmin 을 참조하므로 useCallback 으로 고정한다 — 권한이 늦게 도착(권한 API 응답)해도
    // 그 시점에 아래 useEffect 가 다시 돌아 전사 규칙을 불러온다.
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await mailApi.listMyRules();
            const list = res?.data ?? res ?? [];
            setRules(Array.isArray(list) ? list : []);
        } catch {
            setRules([]);
            notify("차단 목록 조회 실패", "error");
        } finally {
            setLoading(false);
        }

        // 전사 규칙은 권한자에게만. 실패해도 내 목록 표시는 막지 않는다(권한이 없으면 403 이 정상 응답).
        if (!isAdmin) { setGlobalRules([]); return; }
        try {
            const res = await mailApi.listGlobalRules();
            const list = res?.data ?? res ?? [];
            setGlobalRules(Array.isArray(list) ? list : []);
        } catch {
            setGlobalRules([]);
        }
    }, [isAdmin]);

    /** 전사 규칙 활성 토글 — 서버가 MAIL_RULE_GLOBAL 을 재검증한다(프론트 숨김은 편의일 뿐). */
    const handleToggleGlobal = async (rule) => {
        const next = !rule.enabled;
        const ok = await confirm({
            title: next ? "전사 규칙 켜기" : "전사 규칙 끄기",
            message: `모든 직원의 수신 메일에 즉시 반영됩니다.\n${RULE_TYPE_LABEL[rule.ruleType] || rule.ruleType}: ${rule.pattern}`,
            confirmLabel: next ? "켜기" : "끄기",
            variant: next ? "primary" : "danger",
        });
        if (!ok) return;
        try {
            await mailApi.updateGlobalRule(rule.id, { enabled: next });
            notify(next ? "전사 규칙을 켰습니다" : "전사 규칙을 껐습니다", "success");
            await load();
        } catch (e) {
            notify(e?.message || "전사 규칙 변경 실패", "error");
        }
    };

    /** 전사 규칙 삭제 (soft delete) */
    const handleDeleteGlobal = async (rule) => {
        const ok = await confirm({
            title: "전사 차단 규칙 삭제",
            message: `삭제하면 이후 이 패턴의 메일이 전 직원에게 정상 수신됩니다.\n${RULE_TYPE_LABEL[rule.ruleType] || rule.ruleType}: ${rule.pattern}`,
            confirmLabel: "삭제",
            variant: "danger",
        });
        if (!ok) return;
        try {
            await mailApi.deleteGlobalRule(rule.id);
            notify("전사 규칙을 삭제했습니다", "success");
            await load();
        } catch (e) {
            notify(e?.message || "전사 규칙 삭제 실패", "error");
        }
    };

    useEffect(() => {
        load();
    }, [load]);

    const handleUnblock = async (rule) => {
        const label = RULE_TYPE_LABEL[rule.ruleType] || rule.ruleType;
        const ok = await confirm({
            title: "차단 해제",
            message: `차단을 해제할까요?\n${label}: ${rule.pattern}`,
            confirmLabel: "해제",
            variant: "danger",
        });
        if (!ok) return;
        try {
            await mailApi.deleteRule(rule.id);
            notify("차단이 해제되었습니다", "success");
            await load();
        } catch {
            notify("차단 해제 실패", "error");
        }
    };

    return (
        // 스크롤은 이 패널이 담당한다 — 표는 자연 높이로 늘어나고 내부 스크롤을 만들지 않는다(시스템 공통 규칙).
        <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column",
                   overflow: "auto", ...ds.hiddenScrollbar }}>
            {/* 헤더 */}
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <BlockRoundedIcon sx={{ fontSize: 20, color: "#dc2626" }} />
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a", flex: 1 }}>
                    차단 목록
                    <Typography component="span" sx={{ ml: 1, fontSize: 12, color: "#94a3b8" }}>
                        {rules.length}건
                    </Typography>
                </Typography>
                {/* 검색 — 동작 버튼 왼쪽, 우측 상단 고정(전 시스템 공통 배치) */}
                <TableToolbar onSearch={setTableQuery} placeholder="유형·패턴 검색"
                    sx={{ flex: 1, mb: 0, minWidth: 200 }} />
                <Tooltip title="새로고침">
                    <IconButton size="small" onClick={load}>
                        <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Stack>
            <Typography sx={{ fontSize: 12, color: "#64748b", mb: 2 }}>
                메일 상세에서 "발신자 차단"·"도메인 차단" 등으로 추가됩니다. 여기서 일괄 해제할 수 있어요.
            </Typography>

            {/* 테이블 */}
            <Paper variant="outlined" sx={{ borderColor: "#fecaca", overflowX: "clip", overflowY: "visible", flexShrink: 0 }}>
                <ResizableTable size="small" sx={ds.tableSx}>
                    <TableHead>
                        <TableRow>
                            <TableCell width={140}>유형</TableCell>
                            <TableCell>패턴</TableCell>
                            <TableCell width={140}>액션</TableCell>
                            <TableCell width={90}>활성</TableCell>
                            <TableCell align="right" width={70}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#94a3b8" }}><LoadingDots/></TableCell>
                            </TableRow>
                        ) : rulesFiltered.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 6, color: "#94a3b8", fontSize: 13 }}>
                                    차단한 발신자/도메인이 없습니다.
                                    <Typography component="div" sx={{ fontSize: 11, color: "#cbd5e1", mt: 0.5 }}>
                                        스팸 메일을 받으면 메일 상세에서 "발신자 차단" 으로 추가하세요.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : rulesFiltered.map((r) => (
                            <TableRow key={r.id} hover>
                                <TableCell>
                                    <Chip
                                        label={RULE_TYPE_LABEL[r.ruleType] || r.ruleType}
                                        size="small"
                                        sx={{
                                            fontSize: 11, height: 20,
                                            bgcolor: "#fee2e2", color: "#991b1b", fontWeight: 600,
                                        }}
                                    />
                                </TableCell>
                                <TableCell sx={{ fontSize: 13, fontFamily: "monospace", wordBreak: "break-all" }}>
                                    {r.pattern}
                                    {r.note && (
                                        <Typography component="div" sx={{ fontSize: 11, color: "#94a3b8", mt: 0.3 }}>
                                            {r.note}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12, color: "#475569" }}>
                                    {ACTION_LABEL[r.action] || r.action}
                                </TableCell>
                                <TableCell>
                                    {/* 미지원 규칙(BODY_KEYWORD)은 enabled=true 여도 실제로 걸리지 않으므로 '활성' 대신 '미지원' */}
                                    {!isRuleSupported(r) ? (
                                        <Tooltip title="본문 키워드 규칙은 현재 매칭에 사용되지 않습니다. 제목 키워드·정규식으로 대체하세요.">
                                            <Chip label="미지원" size="small"
                                                sx={{ fontSize: 10.5, height: 18, bgcolor: "#fef3c7", color: "#92400e", fontWeight: 700 }} />
                                        </Tooltip>
                                    ) : r.enabled ? (
                                        <Chip label="활성" size="small"
                                            sx={{ fontSize: 10.5, height: 18, bgcolor: "#dcfce7", color: "#166534" }} />
                                    ) : (
                                        <Chip label="비활성" size="small"
                                            sx={{ fontSize: 10.5, height: 18, bgcolor: "#f1f5f9", color: "#64748b" }} />
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title="차단 해제">
                                        <IconButton size="small" onClick={() => handleUnblock(r)}>
                                            <DeleteRoundedIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </ResizableTable>
            </Paper>

            {/*
              전사(GLOBAL) 차단 규칙 — MAIL_RULE_GLOBAL 권한자에게만 노출.

              왜 별도 섹션인가: GLOBAL 규칙은 employee_id 가 NULL 이라 위쪽 '내 차단 목록'(listMyRules)에는
              절대 안 잡힌다. 그래서 서버에 /rules/global CRUD 가 다 있는데도 <b>화면이 없어</b>
              한 번 만든 전사 규칙을 끄거나 지울 방법이 없었다(특히 비활성화하면 목록에서 사라져
              id 를 따로 적어두지 않는 한 DB 를 직접 건드려야 했다 — 그 조회 필터는 이번에 제거했다).

              여기서는 활성 토글과 삭제만 제공한다. 생성은 메일 상세의 차단 버튼(USER)과 성격이 달라
              (전 직원 수신에 영향) 의도적으로 이 화면에 두지 않는다.
            */}
            {isAdmin && (
                <>
                    <Stack direction="row" spacing={1} alignItems="center" mt={4} mb={1}>
                        <PublicRoundedIcon sx={{ fontSize: 20, color: "#b45309" }} />
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a", flex: 1 }}>
                            전사 차단 규칙
                            <Typography component="span" sx={{ ml: 1, fontSize: 12, color: "#94a3b8" }}>
                                {globalRules.length}건
                            </Typography>
                        </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 12, color: "#b45309", mb: 2 }}>
                        ⚠️ 모든 직원의 수신 메일에 적용됩니다. 끄거나 지우면 즉시 전사에 반영됩니다.
                    </Typography>

                    <Paper variant="outlined" sx={{ borderColor: "#fed7aa", overflowX: "clip", overflowY: "visible", flexShrink: 0 }}>
                        <ResizableTable size="small" sx={ds.tableSx}>
                            <TableHead>
                                <TableRow>
                                    <TableCell width={140}>유형</TableCell>
                                    <TableCell>패턴</TableCell>
                                    <TableCell width={140}>액션</TableCell>
                                    <TableCell width={110}>활성</TableCell>
                                    <TableCell align="right" width={70}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {globalRules.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 5, color: "#94a3b8", fontSize: 13 }}>
                                            등록된 전사 차단 규칙이 없습니다.
                                        </TableCell>
                                    </TableRow>
                                ) : globalRules.map((r) => (
                                    <TableRow key={r.id} hover>
                                        <TableCell>
                                            <Chip label={RULE_TYPE_LABEL[r.ruleType] || r.ruleType} size="small"
                                                sx={{ fontSize: 11, height: 20, bgcolor: "#ffedd5", color: "#9a3412", fontWeight: 600 }} />
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13, fontFamily: "monospace", wordBreak: "break-all" }}>
                                            {r.pattern}
                                            {r.note && (
                                                <Typography component="div" sx={{ fontSize: 11, color: "#94a3b8", mt: 0.3 }}>
                                                    {r.note}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 12, color: "#475569" }}>
                                            {ACTION_LABEL[r.action] || r.action}
                                        </TableCell>
                                        <TableCell>
                                            {!isRuleSupported(r) ? (
                                                <Tooltip title="본문 키워드 규칙은 현재 매칭에 사용되지 않습니다.">
                                                    <Chip label="미지원" size="small"
                                                        sx={{ fontSize: 10.5, height: 18, bgcolor: "#fef3c7", color: "#92400e", fontWeight: 700 }} />
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title={r.enabled ? "끄면 이후 수신 메일에 적용되지 않습니다" : "켜면 이후 수신 메일에 적용됩니다"}>
                                                    <Switch size="small" checked={!!r.enabled}
                                                        onChange={() => handleToggleGlobal(r)} />
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="전사 규칙 삭제">
                                                <IconButton size="small" onClick={() => handleDeleteGlobal(r)}>
                                                    <DeleteRoundedIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </ResizableTable>
                    </Paper>
                </>
            )}
        </Box>
    );
}
